import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCtaPress } from './shared';
import type { TemplateProps } from './types';

const SHOTS = ['/store/shot-1.png', '/store/shot-2.webp', '/store/shot-3.png', '/store/shot-4.png'];

export function PlayStoreTemplate({ state, onAccept, appName, logoUrl }: TemplateProps) {
  const { t } = useTranslation();
  const ready = state.status === 'awaiting-click';
  const { pressed, onClick } = useCtaPress(onAccept);

  return (
    <div
      className="min-h-dvh bg-white text-zinc-900 select-none pb-10"
      style={{ fontFamily: "'Google Sans', 'Roboto', -apple-system, sans-serif" }}
    >
      {/* App header */}
      <div className="px-5 pt-7 pb-4">
        <div className="flex gap-4">
          <div
            className="relative h-[84px] w-[84px] shrink-0 rounded-[22px] overflow-hidden shadow-md ring-1 ring-black/5"
            style={
              logoUrl
                ? undefined
                : { background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)' }
            }
          >
            {logoUrl ? (
              <img src={logoUrl} alt={appName || 'App'} className="h-full w-full object-cover" />
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-b from-white/25 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/95 shadow-sm">
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      className="h-5 w-5 translate-x-[1px] text-fuchsia-600"
                    >
                      <path fill="currentColor" d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-[22px] leading-tight font-medium text-zinc-900 truncate">
              {appName || t('playstore.fallbackAppName')}
            </h1>
            <p className="text-sm text-emerald-700 mt-1">{t('playstore.verifiedPublisher')}</p>
            <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1">
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 text-emerald-600">
                <path
                  fill="currentColor"
                  d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"
                />
              </svg>
              {t('playstore.secureDownload')}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="text-base font-semibold flex items-center justify-center gap-0.5">
              4.8 <span className="text-yellow-500 text-sm">★</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {t('playstore.reviews', { amount: '128K' })}
            </div>
          </div>
          <div className="w-px h-9 bg-zinc-200" />
          <div className="text-center flex-1">
            <div className="text-base font-semibold">1M+</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{t('playstore.downloads')}</div>
          </div>
          <div className="w-px h-9 bg-zinc-200" />
          <div className="text-center flex-1">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-[18px] w-[18px] mx-auto text-zinc-700"
            >
              <path
                fill="currentColor"
                d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
              />
            </svg>
            <div className="text-[11px] text-zinc-500 mt-0.5">{t('playstore.editorsPick')}</div>
          </div>
          <div className="w-px h-9 bg-zinc-200" />
          <div className="text-center flex-1">
            <div className="flex items-center justify-center">
              <span className="inline-flex items-center justify-center h-[18px] w-[18px] rounded bg-zinc-900 text-white text-[10px] font-bold">
                L
              </span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{t('playstore.allAges')}</div>
          </div>
        </div>
      </div>

      {/* Install button */}
      <div className="px-5">
        <button
          type="button"
          onClick={onClick}
          className={`
            w-full rounded-full py-3.5 text-[15px] font-semibold transition-all duration-200
            flex items-center justify-center gap-2
            ${
              ready
                ? pressed
                  ? 'bg-emerald-800 text-white scale-[0.98]'
                  : 'bg-emerald-700 text-white active:scale-[0.98] active:bg-emerald-800 shadow-md hover:shadow-lg'
                : 'bg-emerald-700/60 text-white/80'
            }
          `}
        >
          {ready ? (
            <>
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4">
                <path
                  fill="currentColor"
                  d="M13 5v6h1.17L12 13.17 9.83 11H11V5h2m2-2H9v6H5l7 7 7-7h-4V3zm4 15H5v2h14v-2z"
                />
              </svg>
              {t('playstore.install')}
            </>
          ) : (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('playstore.checking')}
            </>
          )}
        </button>
        {ready && (
          <p className="text-center text-[11px] text-zinc-500 mt-2">
            {t('playstore.downloadWithin', { seconds: 10 })}
          </p>
        )}
      </div>

      {/* Action links */}
      <div className="flex items-center justify-center gap-12 px-5 py-5 text-sm font-medium">
        <span className="flex items-center gap-2 text-emerald-700">
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]">
            <path
              fill="currentColor"
              d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"
            />
          </svg>
          {t('playstore.share')}
        </span>
        <span className="flex items-center gap-2 text-emerald-700">
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]">
            <path fill="currentColor" d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" />
          </svg>
          {t('playstore.wishlist')}
        </span>
      </div>

      {/* Screenshots */}
      <div className="px-5 pb-5">
        <div className="flex gap-3 overflow-x-auto pb-1">
          {SHOTS.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`Screenshot ${i + 1}`}
              loading="lazy"
              className="h-48 w-[108px] shrink-0 rounded-xl object-cover ring-1 ring-black/5 shadow-sm"
            />
          ))}
        </div>
      </div>

      {/* About */}
      <div className="px-5 py-4 border-t border-zinc-100">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium">{t('playstore.about')}</h2>
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 text-zinc-400">
            <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </div>
        <p className="text-sm leading-relaxed text-zinc-600 mt-2">{t('playstore.aboutText')}</p>
      </div>
    </div>
  );
}
